import type {
  ApplyTreeMapNodesSelectorProps,
  GetLocationTreeNodeActiveOptionsProps,
  GetTreeMapNodesByValueProps,
  GetTreeMapStatsProps,
  GetTreeNodeActiveOptionsProps,
  IsAssetTreeNodeActiveProps,
  TreeMapFilterType,
  TreeMapNode,
  TreeMapNodeActiveOptions,
  TreeMapNodesByType,
  TreeMapNodeType,
  TreeMapStats,
  TreeMapSubFilters,
  TreeMapSubFiltersCollection,
  TreeMapType,
} from "./tree-map.type";
import type {
  AssetTreeEndpoint,
  AssetTreeNode,
  AssetTreeNodeGeographyDataType,
} from "@/entities/asset-tree";
import type { MapGeojson, MapGeojsonOptions, MapMarker } from "@/modules/map";

import {
  assetTreeNodeHasArea,
  assetTreeNodeHasPerimeter,
  getAssetTreeNodeAssetConditionRating,
} from "@/entities/asset-tree";
import { isAssetTreeNodeSector } from "@/modules/asset-tree";

export const getTreeEndpoint = (type: TreeMapType): AssetTreeEndpoint => {
  return type === "organization" ? "organizations" : "locations";
};

export const getTreeMapNodeType = (node: AssetTreeNode): TreeMapNodeType => {
  return isAssetTreeNodeSector(node) ? "sector" : node.type;
};

export const getTreeNodeActiveOptions = ({
  node,
  filterByType,
  subFilters,
}: GetTreeNodeActiveOptionsProps): TreeMapNodeActiveOptions => {
  if (!filterByType[node.type]) return {};

  if (node.type === "asset") {
    const isActive = isAssetTreeNodeActive({ node, subFilters });
    return { isMarkerActive: isActive };
  }

  if (node.type === "location") {
    return getLocationTreeNodeActiveOptions({ node, subFilters });
  }

  return { isMarkerActive: true };
};

export const isAssetTreeNodeActive = ({
  node,
  subFilters,
}: IsAssetTreeNodeActiveProps): boolean => {
  if (subFilters.conditionRating?.length) {
    const nodeValue = getAssetTreeNodeAssetConditionRating(node);
    if (!subFilters.conditionRating.includes(nodeValue)) {
      return false;
    }
  }

  if (subFilters.assetType?.length) {
    const nodeValue = node.asset_type.id;
    if (!subFilters.assetType.includes(nodeValue)) {
      return false;
    }
  }

  if (subFilters.state?.length) {
    const nodeValue = node.state;
    if (!subFilters.state.includes(nodeValue)) {
      return false;
    }
  }

  return true;
};

export const getLocationTreeNodeActiveOptions = ({
  node,
  subFilters,
}: GetLocationTreeNodeActiveOptionsProps): TreeMapNodeActiveOptions => {
  const isMarkerActive =
    node.position && isTreeMapLocationSubFilterActive(subFilters, "position")
      ? true
      : false;

  const isPerimeterActive =
    assetTreeNodeHasPerimeter(node) &&
    isTreeMapLocationSubFilterActive(subFilters, "perimeter");

  const isAreaActive =
    assetTreeNodeHasArea(node) &&
    isTreeMapLocationSubFilterActive(subFilters, "area");

  return {
    isMarkerActive,
    isPerimeterActive,
    isAreaActive,
  };
};

export const applyTreeMapNodesSelector = ({
  nodes,
  filterByType,
  subFilters,
  getMarker,
}: ApplyTreeMapNodesSelectorProps) => {
  const markers: MapMarker[] = [];
  const geojson: MapGeojson[] = [];
  let hasGeojson = false;

  let nodesByType: TreeMapNodesByType = {
    organization: [],
    location: [],
    sector: [],
    asset: [],
  };

  nodes.forEach((node) => {
    const type = getTreeMapNodeType(node);

    const childrenPayload = applyTreeMapNodesSelector({
      nodes: node.children ?? [],
      filterByType,
      subFilters,
      getMarker,
    });

    const options = getTreeNodeActiveOptions({
      node,
      filterByType,
      subFilters,
    });

    const mapNode: TreeMapNode = { type, node, options };

    // Handle `nodesByType`
    nodesByType[type].push(mapNode);
    nodesByType = mergeNodesByType(nodesByType, childrenPayload.nodesByType);

    // Handle `markers`
    const marker = getMarker(node);
    const childrenMarkers = childrenPayload.markers;
    if (options.isMarkerActive && marker) {
      markers.push({ ...marker, children: childrenMarkers });
    } else if (childrenMarkers.length > 0) {
      markers.push(...childrenMarkers);
    }

    // Handle `geojson` & `hasGeojson`
    const geojsonData = getTreeMapNodeGeojson(node);
    const isGeojsonActive = options.isPerimeterActive || options.isAreaActive;
    if (isGeojsonActive && geojsonData) geojson.push(geojsonData);
    if (childrenPayload.geojson) geojson.push(...childrenPayload.geojson);
    if (geojsonData || childrenPayload.hasGeojson) hasGeojson = true;
  });

  return {
    nodesByType,
    markers,
    geojson,
    hasGeojson,
  };
};

export const mergeNodesByType = (
  payload1: TreeMapNodesByType,
  payload2: TreeMapNodesByType
) => ({
  organization: [...payload1.organization, ...payload2.organization],
  location: [...payload1.location, ...payload2.location],
  sector: [...payload1.sector, ...payload2.sector],
  asset: [...payload1.asset, ...payload2.asset],
});

export const getTreeMapNodeGeojson = (
  node: AssetTreeNode
): MapGeojson | undefined => {
  if (node.type === "location" && node.geographical_data) {
    return {
      key: `location-${node.id}-geojson`,
      data: node.geographical_data,
    };
  }

  return undefined;
};

export const getTreeMapStats = ({
  nodesByType,
  hasGeojson,
}: GetTreeMapStatsProps): TreeMapStats => {
  const organizationLength = nodesByType.organization?.length ?? 0;
  const locationsLength = nodesByType.location?.length ?? 0;
  const sectorsLength = nodesByType.sector?.length ?? 0;
  const assetsLength = nodesByType.asset?.length ?? 0;

  return {
    organizationLength,
    hasOrganizations: !!organizationLength,
    locationsLength,
    hasLocations: !!locationsLength,
    sectorsLength,
    hasSectors: !!sectorsLength,
    assetsLength,
    hasAssets: !!assetsLength,
    hasGeojson,
  };
};

/**
 * See rules from "_Règles d'affaire carte d'un arbre.xlsx_" in Sharepoint
 */
export const hideLocationsFilters = (stats: TreeMapStats): boolean => {
  const { hasSectors, hasGeojson, hasLocations, hasAssets } = stats;

  if (hasLocations && !hasSectors && !hasGeojson) return true;

  if (!hasGeojson && !hasLocations && !hasAssets) return true;

  return false;
};

/**
 * See rules from "_Règles d'affaire carte d'un arbre.xlsx_" in Sharepoint
 */
export const hideAssetsFilters = ({ hasAssets }: TreeMapStats): boolean => {
  return !hasAssets ? true : false;
};

export const transformTreeMapSubFilters = (
  collection: TreeMapSubFiltersCollection
): TreeMapSubFilters => {
  return {
    location: transformTreeMapSubFilterItem(collection.location),
    conditionRating: transformTreeMapSubFilterItem(collection.conditionRating),
    assetType: transformTreeMapSubFilterItem(collection.assetType).map(Number),
    state: transformTreeMapSubFilterItem(collection.state),
  };
};

export const transformTreeMapSubFilterItem = <Key extends string>(
  filterItem?: Partial<Record<Key, boolean>>
): Key[] => {
  if (!filterItem) return [];

  return Object.entries(filterItem)
    .map(([filterKey, isChecked]) => (isChecked ? (filterKey as Key) : null))
    .filter((key) => key !== null);
};

export const getTreeMapGeojsonOptions = (
  subFilters: TreeMapSubFilters
): MapGeojsonOptions => {
  let options: MapGeojsonOptions = {
    hidePerimeters: false,
    hideArea: false,
  };

  if (!isTreeMapLocationSubFilterActive(subFilters, "perimeter")) {
    options = { ...options, hidePerimeters: true };
  }

  if (!isTreeMapLocationSubFilterActive(subFilters, "area")) {
    options = { ...options, hideArea: true };
  }

  return options;
};

export const isTreeMapLocationSubFilterActive = (
  subFilters: TreeMapSubFilters,
  type: AssetTreeNodeGeographyDataType
): boolean => {
  return subFilters.location?.includes(type) ?? false;
};

export const isTreeMapNodeActive = (mapNode: TreeMapNode) => {
  const { options, node } = mapNode;
  const { isMarkerActive, isPerimeterActive, isAreaActive } = options ?? {};

  return node.type === "location"
    ? isMarkerActive || isPerimeterActive || isAreaActive
    : isMarkerActive;
};

export const getTreeMapNodesByValue = <
  Node extends AssetTreeNode,
  Value extends string,
>({
  nodes,
  getTreeNodeValue,
}: GetTreeMapNodesByValueProps<Node, Value>) => {
  const assetsByValue: Partial<Record<Value, TreeMapNode[]>> = {};

  for (const node of nodes) {
    const assetTree = node.node as Node;
    const key = getTreeNodeValue(assetTree);
    assetsByValue[key] = [...(assetsByValue[key] ?? []), node];
  }

  return assetsByValue;
};

export const getFilterActiveNodesCount = (
  type: TreeMapFilterType,
  nodesByType: TreeMapNodesByType
) => {
  const count = getActiveNodesCount(type, nodesByType);

  if (type !== "location") return count;

  // For location, let's add sector nodes
  return count + getActiveNodesCount("sector", nodesByType);
};

const getActiveNodesCount = (
  type: TreeMapNodeType,
  nodesByType: TreeMapNodesByType
) => {
  const nodes = nodesByType[type] ?? [];
  const activeNodes = nodes.filter(isTreeMapNodeActive);
  return activeNodes.length;
};
